import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Search, Edit, Trash2, ArrowLeft, ChefHat, DollarSign, 
  TrendingUp, TrendingDown, RefreshCcw, Calculator, AlertTriangle 
} from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recipes');
  const [showDialog, setShowDialog] = useState(false);
  const [showMenuItemDialog, setShowMenuItemDialog] = useState(false);
  const [foodCostReport, setFoodCostReport] = useState(null);
  
  const [formData, setFormData] = useState({
    menu_item_id: '',
    name: '',
    yield_qty: 1,
    waste_percent: 0,
    notes: '',
    ingredients: []
  });
  
  const [menuItemForm, setMenuItemForm] = useState({
    name: '', sku: '', category: '', sell_price: 0
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [recipesRes, menuRes, invRes, uomRes, reportRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/recipes`, { headers }),
        fetch(`${API_URL}/api/inv/menu-items`, { headers }),
        fetch(`${API_URL}/api/inv/items?limit=200`, { headers }),
        fetch(`${API_URL}/api/inv/uom`, { headers }),
        fetch(`${API_URL}/api/inv/reports/food-cost`, { headers })
      ]);
      
      if (recipesRes.ok) setRecipes(await recipesRes.json());
      if (menuRes.ok) setMenuItems(await menuRes.json());
      if (invRes.ok) setInventoryItems(await invRes.json());
      if (uomRes.ok) setUoms(await uomRes.json());
      if (reportRes.ok) setFoodCostReport(await reportRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateRecipe = async (e) => {
    e.preventDefault();
    if (formData.ingredients.length === 0) {
      alert('Please add at least one ingredient');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/inv/recipes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...formData,
          yield_qty: parseFloat(formData.yield_qty) || 1,
          waste_percent: parseFloat(formData.waste_percent) || 0,
          ingredients: formData.ingredients.map((ing, i) => ({
            item_id: ing.item_id,
            qty: parseFloat(ing.qty),
            uom_id: ing.uom_id || null,
            waste_percent: parseFloat(ing.waste_percent) || 0,
            sort_order: i
          }))
        })
      });
      
      if (res.ok) {
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error creating recipe');
      }
    } catch (err) {
      console.error('Error creating recipe:', err);
    }
  };

  const handleCreateMenuItem = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/inv/menu-items`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...menuItemForm,
          sell_price: parseFloat(menuItemForm.sell_price) || 0
        })
      });
      
      if (res.ok) {
        setShowMenuItemDialog(false);
        setMenuItemForm({ name: '', sku: '', category: '', sell_price: 0 });
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error creating menu item');
      }
    } catch (err) {
      console.error('Error creating menu item:', err);
    }
  };

  const recalculateRecipe = async (recipeId) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/recipes/${recipeId}/recalculate`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error recalculating:', err);
    }
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { item_id: '', qty: 1, uom_id: '', waste_percent: 0 }]
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngs = [...formData.ingredients];
    newIngs[index][field] = value;
    setFormData({ ...formData, ingredients: newIngs });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const resetForm = () => {
    setFormData({
      menu_item_id: '',
      name: '',
      yield_qty: 1,
      waste_percent: 0,
      notes: '',
      ingredients: []
    });
  };

  const getFoodCostColor = (percent) => {
    if (percent > 35) return 'text-red-400';
    if (percent > 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="recipes-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Recipes & Food Cost</h1>
          <p className="text-gray-400">Manage recipes and track food costs</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {foodCostReport && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-blue-700/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Menu Items with Recipes</p>
                  <p className="text-2xl font-bold text-white">{foodCostReport.summary.total_items_with_recipes}</p>
                </div>
                <ChefHat className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-green-700/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Avg Food Cost %</p>
                  <p className={`text-2xl font-bold ${getFoodCostColor(foodCostReport.summary.avg_food_cost_percent)}`}>
                    {foodCostReport.summary.avg_food_cost_percent?.toFixed(1)}%
                  </p>
                </div>
                <Calculator className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-purple-700/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Food Cost</p>
                  <p className="text-2xl font-bold text-white">
                    ${foodCostReport.summary.total_food_cost?.toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20 border-yellow-700/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Potential Revenue</p>
                  <p className="text-2xl font-bold text-white">
                    ${foodCostReport.summary.total_potential_revenue?.toFixed(2)}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800/50 border border-gray-700">
          <TabsTrigger value="recipes" className="data-[state=active]:bg-blue-600">
            Recipes ({recipes.length})
          </TabsTrigger>
          <TabsTrigger value="menu-items" className="data-[state=active]:bg-blue-600">
            Menu Items ({menuItems.length})
          </TabsTrigger>
          <TabsTrigger value="food-cost" className="data-[state=active]:bg-blue-600">
            Food Cost Analysis
          </TabsTrigger>
        </TabsList>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="mt-4">
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">All Recipes</CardTitle>
                <Dialog open={showDialog} onOpenChange={(open) => {
                  setShowDialog(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-recipe-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Recipe
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create Recipe</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateRecipe} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300">Menu Item *</Label>
                          <Select
                            value={formData.menu_item_id}
                            onValueChange={(v) => setFormData({ ...formData, menu_item_id: v })}
                          >
                            <SelectTrigger className="bg-gray-800 border-gray-700">
                              <SelectValue placeholder="Select menu item" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700">
                              {menuItems.map(item => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name} (${item.sell_price})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-gray-300">Recipe Name (optional)</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="bg-gray-800 border-gray-700"
                            placeholder="e.g., Standard Recipe"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Yield Quantity</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.yield_qty}
                            onChange={(e) => setFormData({ ...formData, yield_qty: e.target.value })}
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Waste % (recipe level)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={formData.waste_percent}
                            onChange={(e) => setFormData({ ...formData, waste_percent: e.target.value })}
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                      </div>

                      {/* Ingredients */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-gray-300">Ingredients</Label>
                          <Button type="button" size="sm" variant="outline" onClick={addIngredient}>
                            <Plus className="w-4 h-4 mr-1" /> Add Ingredient
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {formData.ingredients.map((ing, index) => (
                            <div key={index} className="flex gap-2 items-center p-2 bg-gray-800/50 rounded">
                              <Select
                                value={ing.item_id}
                                onValueChange={(v) => updateIngredient(index, 'item_id', v)}
                              >
                                <SelectTrigger className="flex-1 bg-gray-800 border-gray-700">
                                  <SelectValue placeholder="Select ingredient" />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gray-700 max-h-[200px]">
                                  {inventoryItems.map(item => (
                                    <SelectItem key={item.id} value={item.id}>
                                      {item.name} (${item.avg_unit_cost?.toFixed(4)}/unit)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Qty"
                                value={ing.qty}
                                onChange={(e) => updateIngredient(index, 'qty', e.target.value)}
                                className="w-20 bg-gray-800 border-gray-700"
                              />
                              <Input
                                type="number"
                                step="0.1"
                                placeholder="Waste%"
                                value={ing.waste_percent}
                                onChange={(e) => updateIngredient(index, 'waste_percent', e.target.value)}
                                className="w-20 bg-gray-800 border-gray-700"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeIngredient(index)}
                                className="text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          {formData.ingredients.length === 0 && (
                            <p className="text-gray-400 text-center py-4">No ingredients added</p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={!formData.menu_item_id || formData.ingredients.length === 0}>
                          Create Recipe
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="text-left p-4 text-gray-400 font-medium">Menu Item</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Version</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Yield</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Food Cost</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Cost/Unit</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Last Calc</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map(recipe => (
                      <tr key={recipe.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                        <td className="p-4">
                          <p className="text-white font-medium">{recipe.menu_item_name}</p>
                          {recipe.name && <p className="text-sm text-gray-400">{recipe.name}</p>}
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">v{recipe.version_no}</Badge>
                        </td>
                        <td className="p-4 text-right text-gray-400">{recipe.yield_qty}</td>
                        <td className="p-4 text-right text-white font-medium">
                          ${recipe.calculated_cost?.toFixed(2)}
                        </td>
                        <td className="p-4 text-right text-gray-400">
                          ${recipe.cost_per_unit?.toFixed(4)}
                        </td>
                        <td className="p-4 text-center text-gray-400 text-sm">
                          {recipe.last_calculated_at 
                            ? new Date(recipe.last_calculated_at).toLocaleDateString() 
                            : '-'}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => recalculateRecipe(recipe.id)}
                              title="Recalculate cost"
                            >
                              <RefreshCcw className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {recipes.length === 0 && (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-400">
                          {loading ? 'Loading...' : 'No recipes found. Create your first recipe!'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Menu Items Tab */}
        <TabsContent value="menu-items" className="mt-4">
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-white">Menu Items</CardTitle>
                  <CardDescription>Items that can have recipes attached</CardDescription>
                </div>
                <Dialog open={showMenuItemDialog} onOpenChange={setShowMenuItemDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" /> Add Menu Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-white">Add Menu Item</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateMenuItem} className="space-y-4">
                      <div>
                        <Label className="text-gray-300">Name *</Label>
                        <Input
                          value={menuItemForm.name}
                          onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
                          className="bg-gray-800 border-gray-700"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300">SKU</Label>
                          <Input
                            value={menuItemForm.sku}
                            onChange={(e) => setMenuItemForm({ ...menuItemForm, sku: e.target.value })}
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Category</Label>
                          <Input
                            value={menuItemForm.category}
                            onChange={(e) => setMenuItemForm({ ...menuItemForm, category: e.target.value })}
                            className="bg-gray-800 border-gray-700"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300">Sell Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={menuItemForm.sell_price}
                          onChange={(e) => setMenuItemForm({ ...menuItemForm, sell_price: e.target.value })}
                          className="bg-gray-800 border-gray-700"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowMenuItemDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">Create</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="text-left p-4 text-gray-400 font-medium">Name</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Category</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Sell Price</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Recipe Cost</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Margin %</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menuItems.map(item => (
                      <tr key={item.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                        <td className="p-4">
                          <p className="text-white font-medium">{item.name}</p>
                          {item.sku && <p className="text-sm text-gray-400">{item.sku}</p>}
                        </td>
                        <td className="p-4 text-gray-400">{item.category || '-'}</td>
                        <td className="p-4 text-right text-white">${item.sell_price?.toFixed(2)}</td>
                        <td className="p-4 text-right text-gray-400">
                          {item.recipe_cost ? `$${item.recipe_cost.toFixed(2)}` : '-'}
                        </td>
                        <td className="p-4 text-right">
                          {item.margin_percent !== null && item.margin_percent !== undefined ? (
                            <span className={getFoodCostColor(100 - item.margin_percent)}>
                              {item.margin_percent.toFixed(1)}%
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-4 text-center">
                          {item.recipe_cost ? (
                            <Badge className="bg-green-600">Has Recipe</Badge>
                          ) : (
                            <Badge variant="secondary">No Recipe</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                    {menuItems.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-400">
                          No menu items found. Add your first menu item!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Food Cost Analysis Tab */}
        <TabsContent value="food-cost" className="mt-4">
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Food Cost Analysis
              </CardTitle>
              <CardDescription>Items sorted by food cost % (highest first - may need attention)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="text-left p-4 text-gray-400 font-medium">Menu Item</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Category</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Sell Price</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Food Cost</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Food Cost %</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Margin</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {foodCostReport?.items?.map(item => (
                      <tr key={item.menu_item_id} className="border-t border-gray-700 hover:bg-gray-800/30">
                        <td className="p-4 text-white font-medium">{item.name}</td>
                        <td className="p-4 text-gray-400">{item.category || '-'}</td>
                        <td className="p-4 text-right text-white">${item.sell_price?.toFixed(2)}</td>
                        <td className="p-4 text-right text-gray-400">${item.food_cost?.toFixed(2)}</td>
                        <td className={`p-4 text-right font-medium ${getFoodCostColor(item.food_cost_percent)}`}>
                          {item.food_cost_percent?.toFixed(1)}%
                        </td>
                        <td className="p-4 text-right text-white">${item.margin?.toFixed(2)}</td>
                        <td className={`p-4 text-right font-medium ${item.margin_percent > 60 ? 'text-green-400' : item.margin_percent > 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {item.margin_percent?.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                    {(!foodCostReport?.items || foodCostReport.items.length === 0) && (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-400">
                          No items with recipes found. Create recipes to see food cost analysis.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
